import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { find, isNil, map } from 'lodash';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { LampOptions } from '../../interfaces/LampOptions';
import { AuthService } from '@app/shared/services/auth.service';
import { MapService } from '../../services/map.service';
import { MapStoreService } from '../../services/map-store.service';
import { LampsSelectedService } from '../../services/lamps-selected.service';
import { environment } from '@env/environment';
import { NotificationsService } from '@app/shared/notifications/notifications.service';
import { DescriptiveNotificationComponent } from '@app/shared/notifications/descriptive-notification/descriptive-notification.component';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  @Input() lamps: any;
  @Output() showDetail = new EventEmitter();
  @Output() showProductCatalog = new EventEmitter();
  @Output() modelRemoved = new EventEmitter();
  @Output() setLampType = new EventEmitter();

  public number = '';
  public manufacturer = '';
  public wattage = '';
  public luminousColour = '';
  public luminousFlux!: number;
  public warrantyPeriod = '';
  public energySaving = '';
  public co2Saving = '';
  public isLuminous4000K = true;
  public lampImage?: Observable<SafeUrl>;
  public cost = '';
  public lampName = '';
  public lampType = '';
  public isValidImage = false;
  public isKam = false;
  public selectedLamp: any;
  public lampGroup: any;
  public defaultLampImage = null;

  public btnText = 'Wählen Sie eine wärmere Lichtfarbe';

  constructor(
    private authService: AuthService,
    private azureBlobStorageService: AzureBlobStorageService,
    private lampsSelectedService: LampsSelectedService,
    private mapService: MapService,
    private mapStoreService: MapStoreService,
    private matDialog: MatDialog,
    private notificationsService: NotificationsService
  ) {
    this.notificationsService.destroy();
  }

  public ngOnInit(): void {
    this.isKam = this.authService.isKAM;
    this.isLuminous4000K = !isNil(this.lamps[0].preferredLuminosity) ? this.lamps[0].preferredLuminosity === '4000' : true;
    this.lampGroup = this.lamps;
    this.number = this.lamps.length;
    this.manufacturer = this.lamps[0].suggestedManufacturer;
    this.wattage = this.lamps[0].suggestedWatts;
    this.luminousColour = this.isLuminous4000K ? '4000' : '3000';
    this.luminousFlux = this.isLuminous4000K ? this.lamps[0].suggestedLuminousFlux4000K : this.lamps[0].suggestedLuminousFlux3000K;
    this.warrantyPeriod = this.lamps[0].suggestedGuaranteeYears;
    this.energySaving = this.lamps[0].energySavings;
    this.co2Saving = this.lamps[0].cO2Savings;
    this.lampType = this.lamps[0].lightType;

    this.btnText = this.isLuminous4000K ? 'Wählen Sie eine wärmere Lichtfarbe' : 'Wählen Sie eine kältere Lichtfarbe';

    if (!isNil(this.lamps[0].image)) {
      if (this.lamps[0].image.includes(environment.proxyApiURL)) {
        this.lampImage = this.lamps[0].image;
      } else {
        this.lampImage = this.azureBlobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/images/lamps/${this.lamps[0].image}`);
      }
    } else {
      this.lampImage = of();
    }

    if (this.isKam) {
      this.cost = this.lamps[0].salesPrice;
    } else {
      this.cost = this.lamps[0].uvpCost;
    }
    this.lampName = this.lamps[0].suggestedModel;

    this.lampsSelectedService.isProductDetailMode = true;
    this.isValidImage = !isNil(this.lampImage);
    this.matDialog.closeAll();
  }

  public ngOnDestroy(): void {
    this.notificationsService.destroy();
  }

  toggleLuminousity() {
    this.isLuminous4000K = !this.isLuminous4000K;
    this.luminousFlux = this.isLuminous4000K ? this.lamps[0].suggestedLuminousFlux4000K : this.lamps[0].suggestedLuminousFlux3000K;
    this.luminousColour = this.isLuminous4000K ? '4000' : '3000';

    this.btnText = this.isLuminous4000K ? 'Wählen Sie eine wärmere Lichtfarbe' : 'Wählen Sie eine kältere Lichtfarbe';
  }

  close() {
    this.lampsSelectedService.resetProductDetailLamps();
    this.showDetail.emit(false);
  }

  showCatalog() {
    this.setLampType.emit(this.lampGroup);
    this.showProductCatalog.emit(true);
    this.showDetail.emit(false);
  }

  removeLampGroup(value: any[]) {
    value.forEach((element) => this.updateLampClass(element, { isSelected: false, isHighlighted: false }));
    this.lampsSelectedService.removeLamps(value);
  }

  updateLampClass(element: any, options: LampOptions) {
    const selectedLamp = find(this.mapStoreService.lampMarkers, (marker) => marker.options.data.assetId === element.assetId);

    if (!isNil(selectedLamp)) {
      this.mapService.setClassLamp(selectedLamp, options);
    }
  }

  removeLamps() {
    this.notificationsService.destroy();
    this.notificationsService.open(DescriptiveNotificationComponent, {
      model: {
        description: 'Lampen wurde nicht ausgewählt.',
      },
    });

    this.removeLampGroup(this.lamps);
    this.close();
  }

  addLampsToGroup() {
    this.lamps[0].preferredLuminosity = this.luminousColour;

    const product = {
      active: false,
      image: this.lamps[0].image,
      uvpCost: this.lamps[0].uvpCost,
      count: 0,
      suggestedManufacturer: this.lamps[0].suggestedManufacturer,
      suggestedModel: this.lamps[0].suggestedModel,
      energySavings: this.lamps[0].energySavings,
      suggestedCharacterNo: this.lamps[0].suggestedCharacterNo,
      suggestedWatts: this.lamps[0].suggestedWatts,
      suggestedGuaranteeYears: this.lamps[0].suggestedGuaranteeYears,
      costSavings: this.lamps[0].costSavings,
      cO2Savings: this.lamps[0].cO2Savings,
      suggestedLuminousFlux4000K: this.lamps[0].suggestedLuminousFlux4000K,
      suggestedLuminousFlux3000K: this.lamps[0].suggestedLuminousFlux3000K,
      productId: this.lamps[0].productId,
      preferredLuminosity: this.lamps[0].preferredLuminosity,
    };

    let lamps = [];

    lamps = map(this.lampGroup, (element: any) => {
      return Object.assign({}, product, {
        assetId: element.assetId,
        equipment: element.equipment,
        currentManufacturer: element.currentManufacturer,
        currentModel: element.currentModel,
        age: element.age,
      });
    });

    this.lampsSelectedService.updateLamps(lamps);
    this.close();
  }

  setDefaultImage() {
    this.lampImage = this.azureBlobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/images/lamps/DefaultLamp.png`);
  }
}
